import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ProductGroup } from '@app/core/models/client';
import { NewGenericPhoneComponent } from '@app/shared/new-generic-phone/new-generic-phone.component';
import { ProductGroupPreviewReactContainerComponent } from '@app/client-config/product-group/components/product-group-preview-react-container/product-group-preview-react-container.component';
import { WirePreviewReactContainerComponent } from '@app/shared/phone/wire-preview-react-container/wire-preview-react-container.component';
import { SpinnerComponent } from '@app/shared/spinner/spinner.component';

import { MessageClass } from '@app/core/models/message';
import { FocusAiService } from '../../services/focus-ai.service';

@Component({
  selector: 'rn-focus-ai-preview',
  standalone: true,
  imports: [
    NewGenericPhoneComponent,
    ProductGroupPreviewReactContainerComponent,
    WirePreviewReactContainerComponent,
    SpinnerComponent,
  ],
  templateUrl: './focus-ai-preview.component.html',
  styleUrl: './focus-ai-preview.component.scss',
})
export class FocusAiPreviewComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) productGroup: ProductGroup;
  @Input() language: string = 'en';
  @Input({ required: true }) message: MessageClass | null = null;
  @Input() errorMessage: string = '';
  @Input() instructionText: string = '';
  @Input() phoneSize: 'small' | 'medium' | 'large' = 'small';
  @Input() minResponsivePhoneWidth?: number;

  @Input({ required: true }) feedback: 'positive' | 'negative' | null;
  @Output() feedbackChange = new EventEmitter<'positive' | 'negative' | null>();
  focusAiService = inject(FocusAiService);

  @ViewChild('phoneFrame') phoneFrame?: ElementRef<HTMLElement>;
  @ViewChild('genericPhone', { read: ElementRef })
  genericPhone?: ElementRef<HTMLElement>;

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private resizeObserver?: ResizeObserver;

  get isPhoneResponsive(): boolean {
    return this.minResponsivePhoneWidth != null;
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updatePhoneScale());
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    this.updatePhoneScale();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['phoneSize'] || changes['minResponsivePhoneWidth']) {
      this.updatePhoneScale();
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  sendFeedBack(feedback: 'positive' | 'negative') {
    this.feedbackChange.emit(feedback);
  }

  private updatePhoneScale(): void {
    const phoneFrame = this.phoneFrame?.nativeElement;
    if (!phoneFrame) {
      return;
    }

    if (!this.isPhoneResponsive) {
      phoneFrame.style.setProperty('--phone-scale', '1');
      phoneFrame.style.removeProperty('height');
      phoneFrame.style.removeProperty('width');
      return;
    }

    const phoneElement =
      this.genericPhone?.nativeElement.querySelector<HTMLElement>('.phone');
    if (!phoneElement?.offsetWidth || !phoneElement?.offsetHeight) {
      return;
    }

    const phoneWidth = phoneElement.offsetWidth;
    const phoneHeight = phoneElement.offsetHeight;
    const minimumWidth = Math.min(
      Math.max(this.minResponsivePhoneWidth ?? 1, 1),
      phoneWidth,
    );
    const minScale = minimumWidth / phoneWidth;
    const availableWidth = this.elementRef.nativeElement.clientWidth;
    const scale = Math.max(
      minScale,
      Math.min(1, availableWidth / phoneWidth),
    );

    phoneFrame.style.setProperty('--phone-scale', scale.toFixed(4));
    phoneFrame.style.setProperty('height', `${phoneHeight * scale}px`);
    phoneFrame.style.setProperty('width', `${phoneWidth * scale}px`);
  }
}
